import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/_services/auth.service';
import { AI_ASSISTANT_NAME } from 'src/app/_core/config/app-config.const';
import { AnalyticsPeriod } from '../../models/analytics.types';
import {
  AiInsightProvider,
  AiInsightRow,
  AiInsightSummary,
  AiInsightCriteria,
  AiInsightTangramOption,
  AiInsightsAnalyticsResponse,
  ProfessorAnalyticsService,
} from '../../services/professor-analytics.service';

@Component({
  selector: 'tangram-pa-ai-insights-tab',
  templateUrl: './ai-insights-tab.component.html',
  styleUrls: ['./ai-insights-tab.component.scss']
})
export class AiInsightsTabComponent implements OnInit, OnChanges, OnDestroy {
  // Period flows down from the parent — single source of truth for the
  // dashboard-wide period dropdown.
  @Input() period: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  loading = false;
  generated = false;
  error = '';

  // Provider that produced the currently rendered insights. Tracks which
  // button was clicked so the UI can show "Generated by Gemini" once the
  // response lands. We also remember whether a request is in flight per
  // provider (`pendingProvider`) so each button shows its own spinner.
  activeProvider: AiInsightProvider | '' = '';
  pendingProvider: AiInsightProvider | '' = '';

  aiInsights: AiInsightRow[] = [];
  summary: AiInsightSummary = {
    eligibleTangrams: 0,
    totalViews: 0,
    totalUniqueStudents: 0,
  };
  criteria: AiInsightCriteria = {
    minViewsForTangram: 5,
    minViewsForDiscussion: 5,
    newTangramCount: 3,
    dropOffCompletionThreshold: 40,
  };
  generatedAt = '';

  // Per-tangram filter. Empty string = catalogue mode (all tangrams).
  // Pre-seeded from the `pathwayId` query param so the filter persists when
  // the user lands here from the Tangram page's "Dashboard" button.
  tangrams: AiInsightTangramOption[] = [];
  selectedPathwayId = '';

  // The last scope the server actually computed for. Distinct from
  // `selectedPathwayId` because the server may auto-fall back from a
  // single-Tangram request to catalogue mode when the chosen Tangram has
  // no signal — `appliedPathwayId` reflects what was rendered, not what
  // the user picked.
  appliedPathwayId = '';
  // The chat widget below the insights echoes the selected tangram's title
  // back to the user; thin alias over `scopeDisplayTitle` so existing
  // template bindings keep working.
  get selectedTangramTitle(): string {
    return this.scopeDisplayTitle;
  }

  // Auth context for the embedded ai-chat widget — chat enforces an active
  // subscription per its own check and we forward the same flag.
  subscriptionActive = false;

  // Cancel in-flight request before starting a new one — protects against
  // a fast period switch overwriting fresh data with stale.
  private fetchSub?: Subscription;
  // Tangram-list bootstrap fetch — runs once on init (silent, deterministic
  // provider) so the dropdown is populated before the user picks a provider.
  private bootstrapSub?: Subscription;
  private routeSub?: Subscription;

  constructor(
    private analyticsService: ProfessorAnalyticsService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
  ) {
    // currentUser is typed `User` but `subscriptionActive` is set at runtime
    // by the API and not modelled on the User class — the dashboard component
    // does the same `as any` cast (see dashboard.component.ts:85).
    const user: any = this.authService.currentUserValue;
    this.subscriptionActive = !!(user && user.subscriptionActive);
  }

  ngOnInit(): void {
    // Load the dropdown list of eligible Tangrams once on entry. We use the
    // deterministic provider (no LLM call) so the bootstrap is cheap and
    // doesn't render any narrative — the user still has to click Generate.
    this.bootstrapTangramList();

    // Pre-select the Tangram passed via `?pathwayId=...` (set by the
    // Dashboard button on the Tangram detail page). Subscribed because the
    // user can paste a different deep link without remounting the tab.
    this.routeSub = this.route.queryParams.subscribe((params) => {
      const pid = (params['pathwayId'] || '').toString();
      if (pid !== this.selectedPathwayId) {
        this.selectedPathwayId = pid;
        // If insights have already been generated, re-run with the new scope.
        // Otherwise the next user click will pick up the updated value.
        if (this.generated && this.activeProvider) {
          this.trigger(this.activeProvider as AiInsightProvider);
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the parent period or custom date range changes after insights have been
    // generated, re-run the analysis with the same provider so the rendered
    // narrative tracks the active window without changing the LLM.
    if ((changes['period'] || changes['startDate'] || changes['endDate']) &&
        !(changes['period'] && changes['period'].firstChange) && this.generated && this.activeProvider) {
      this.trigger(this.activeProvider);
    }
  }

  ngOnDestroy(): void {
    if (this.fetchSub) { this.fetchSub.unsubscribe(); }
    if (this.bootstrapSub) { this.bootstrapSub.unsubscribe(); }
    if (this.routeSub) { this.routeSub.unsubscribe(); }
  }

  // Every user-facing label goes through this — the UI never names the
  // underlying model. See AI_ASSISTANT_NAME in app-config.const.ts.
  readonly aiName = AI_ASSISTANT_NAME;

  // The pretty label rendered next to the rank pill / summary bar so the
  // user always knows what produced the visible narrative. 'deterministic'
  // stays unlabelled — that narrative came from the rule engine, not an LLM.
  get activeProviderLabel(): string {
    if (this.activeProvider === 'claude') return this.aiName;
    if (this.activeProvider === 'gemini') return 'Gemini';
    return '';
  }

  isPending(provider: AiInsightProvider): boolean {
    return this.loading && this.pendingProvider === provider;
  }

  // Sync the URL when the user picks a tangram so the deep link stays
  // truthful and a refresh keeps the same scope. Re-fetches if insights
  // are already on screen — otherwise waits for the next Generate click.
  onTangramFilterChange(pathwayId: string): void {
    this.selectedPathwayId = pathwayId || '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pathwayId: this.selectedPathwayId || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (this.generated && this.activeProvider) {
      this.trigger(this.activeProvider as AiInsightProvider);
    }
  }

  trigger(provider: AiInsightProvider): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    this.loading = true;
    this.pendingProvider = provider;
    this.error = '';

    // Remember what the user asked for — needed so the empty-state copy
    // can talk about the selected Tangram even when the server falls
    // back to catalogue mode.
    const requestedPathwayId = this.selectedPathwayId;

    this.fetchSub = this.analyticsService
      .getAiInsightsAnalytics(this.period, requestedPathwayId, provider, this.startDate, this.endDate)
      .subscribe(
        (data: AiInsightsAnalyticsResponse) => {
          this.aiInsights = data.insights || [];
          this.summary = data.summary || this.summary;
          this.criteria = data.criteria || this.criteria;
          this.generatedAt = data.generatedAt || new Date().toISOString();
          this.activeProvider = data.provider || provider;
          // Keep the dropdown options in sync with the latest server view.
          if (data.tangrams && data.tangrams.length) {
            this.tangrams = data.tangrams;
          }
          // The scope the server actually rendered for. May differ from the
          // requested one when the server transparently falls back.
          this.appliedPathwayId = data.filter ? (data.filter.pathwayId || '') : requestedPathwayId;
          // Publish to the per-tab export cache (see service comment).
          this.analyticsService.setLatestTabData('ai-insights', data);
          this.loading = false;
          this.pendingProvider = '';
          this.generated = true;
        },
        (err) => {
          this.error = (err && err.message) ? err.message : 'Failed to generate AI Insights.';
          this.loading = false;
          this.pendingProvider = '';
        },
      );
  }

  // The Tangram name to show in the empty-state and chat scope label.
  // Reflects the user's current pick — falls back to a generic label
  // only when the dropdown options haven't loaded yet.
  get scopeDisplayTitle(): string {
    const pid = this.selectedPathwayId;
    if (!pid) { return 'All Tangrams'; }
    const match = this.tangrams.find((t) => t.pathwayId === pid);
    return match ? match.title : 'Selected Tangram';
  }

  // True when the user picked a single Tangram. Drives the empty-state
  // copy split (per-Tangram vs catalogue).
  get isSingleTangramScope(): boolean {
    return !!this.selectedPathwayId;
  }

  // Fallback "context" sent to the chat backend before the user has
  // generated any insights. Keeps the chat in insights-scope mode (so
  // the assistant doesn't fall back to general tangram chat) and tells
  // it to redirect the user to the Generate buttons above.
  readonly insightsChatPlaceholder =
    `No AI insights have been generated for this scope yet. ` +
    `If the user asks anything, politely tell them to click ` +
    `"Generate with ${AI_ASSISTANT_NAME}" above to load ` +
    `the insights first, then come back to chat about them. Refuse to ` +
    `answer any other question.`;

  // Plain-text snapshot of the rendered insights — passed to the embedded
  // ai-chat widget so the backend can lock the conversation strictly to
  // this content and refuse off-topic questions.
  get insightsContextText(): string {
    if (!this.aiInsights || !this.aiInsights.length) { return ''; }
    const lines: string[] = [];
    lines.push(`Scope: ${this.selectedTangramTitle}`);
    lines.push(`Period: ${this.period}`);
    if (this.activeProvider) { lines.push(`Provider: ${this.activeProvider}`); }
    lines.push(
      `Summary: ${this.summary.eligibleTangrams} Tangram(s) analyzed, ` +
      `${this.summary.totalViews} total views, ` +
      `${this.summary.totalUniqueStudents} unique viewers.`
    );
    lines.push('');
    lines.push('Insights:');
    this.aiInsights.forEach((row) => {
      lines.push(`#${row.rank} [${row.category}] (${row.impact} impact) — ${row.insight}`);
    });
    return lines.join('\n');
  }

  // Bootstrap the dropdown list. Uses deterministic provider so the
  // backend skips the LLM call but still echoes back its `tangrams` array.
  private bootstrapTangramList(): void {
    if (this.bootstrapSub) { this.bootstrapSub.unsubscribe(); }
    this.bootstrapSub = this.analyticsService
      .getAiInsightsAnalytics(this.period, '', 'deterministic', this.startDate, this.endDate)
      .subscribe(
        (data: AiInsightsAnalyticsResponse) => {
          this.tangrams = data.tangrams || [];
        },
        () => { /* swallow — the trigger fetch will surface a real error */ },
      );
  }
}
